import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  PlanPolicies,
  PolicyIncidentDto,
  SubscriptionDto,
  SubscriptionTransferDto,
  TransferCollectionStatusDto,
} from '@cpm/shared';
import { AlertTriangle, DatabaseZap } from 'lucide-react';
import { useState } from 'react';
import { api } from '../api/client.js';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorNote,
  Input,
  Label,
  PageHeader,
  Select,
  Spinner,
  statusTone,
} from '../components/ui.js';

interface PolicyDetail {
  policies: PlanPolicies;
  transfer: SubscriptionTransferDto;
  incidents: PolicyIncidentDto[];
  deploymentBlocked: boolean;
  deploymentBlockedReason: string | null;
  policySuspended: boolean;
  policySuspensionReason: string | null;
}

const bytes = (value: number | null) =>
  value === null
    ? 'Unlimited'
    : new Intl.NumberFormat(undefined, { notation: 'compact', maximumFractionDigits: 2 }).format(
        value,
      );

const exactBytes = (value: number) => `${new Intl.NumberFormat().format(value)} B`;

const policyDateTime = (value: string, timeZone: string) =>
  new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone,
    timeZoneName: 'short',
  }).format(new Date(value));

export function ServicePoliciesPage() {
  const queryClient = useQueryClient();
  const [selectedId, setSelectedId] = useState('');
  const [expiresOn, setExpiresOn] = useState('');
  const [extensionNote, setExtensionNote] = useState('');
  const subscriptions = useQuery({
    queryKey: ['subscriptions'],
    queryFn: () => api.get<SubscriptionDto[]>('/api/subscriptions/'),
  });
  const status = useQuery({
    queryKey: ['service-policy-status'],
    queryFn: () => api.get<TransferCollectionStatusDto>('/api/service-policy/status'),
  });
  const detail = useQuery({
    queryKey: ['service-policy-subscription', selectedId],
    queryFn: () => api.get<PolicyDetail>(`/api/service-policy/subscriptions/${selectedId}`),
    enabled: selectedId !== '',
  });
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['service-policy-status'] });
    void queryClient.invalidateQueries({ queryKey: ['service-policy-subscription'] });
    void queryClient.invalidateQueries({ queryKey: ['subscriptions'] });
  };
  const collect = useMutation({
    mutationFn: () => api.post('/api/service-policy/collect', {}),
    onSuccess: refresh,
  });
  const evaluate = useMutation({
    mutationFn: () => api.post('/api/service-policy/evaluate', {}),
    onSuccess: refresh,
  });
  const acknowledge = useMutation({
    mutationFn: ({ id, note }: { id: string; note: string }) =>
      api.post(`/api/service-policy/incidents/${id}/acknowledge`, { note }),
    onSuccess: refresh,
  });
  const extend = useMutation({
    mutationFn: () =>
      api.post(`/api/service-policy/subscriptions/${selectedId}/extend`, {
        expiresOn,
        note: extensionNote,
        reactivate: true,
      }),
    onSuccess: () => {
      setExpiresOn('');
      setExtensionNote('');
      refresh();
    },
  });
  const reactivate = useMutation({
    mutationFn: () => {
      const note = window.prompt('Why is this subscription safe to reactivate?');
      if (!note) throw new Error('A reactivation note is required');
      return api.post(`/api/service-policy/subscriptions/${selectedId}/reactivate`, { note });
    },
    onSuccess: refresh,
  });

  const mutationError =
    collect.error ?? evaluate.error ?? acknowledge.error ?? extend.error ?? reactivate.error;
  const transferValue = (value: number) =>
    detail.data?.transfer.availability === 'no_data' ? 'No data' : bytes(value);
  const transferTimeZone =
    detail.data?.policies.monthly_transfer?.kind === 'monthly-transfer'
      ? detail.data.policies.monthly_transfer.timeZone
      : 'UTC';

  return (
    <div>
      <PageHeader
        title="Service Policies"
        description="Expiry, monthly transfer, soft thresholds, evidence gaps, and reversible enforcement."
        actions={
          <div className="flex gap-2">
            <Button variant="secondary" loading={evaluate.isPending} onClick={() => evaluate.mutate()}>
              Evaluate now
            </Button>
            <Button loading={collect.isPending} onClick={() => collect.mutate()}>
              Collect counters
            </Button>
          </div>
        }
      />

      {mutationError != null && (
        <div className="mb-4">
          <ErrorNote error={mutationError} />
        </div>
      )}

      <div className="mb-4 grid gap-4 sm:grid-cols-4">
        <Card className="p-4">
          <p className="text-xs uppercase text-slate-500">Collector</p>
          <p className="mt-1 font-semibold">{status.data?.running ? 'Running' : 'Idle'}</p>
        </Card>
        <Card className="p-4">
          <p className="text-xs uppercase text-slate-500">Last result</p>
          <p className="mt-1 font-semibold">{status.data?.latestRun?.status ?? 'No run yet'}</p>
        </Card>
        <Card className="p-4">
          <p className="text-xs uppercase text-slate-500">Samples retained</p>
          <p className="mt-1 font-semibold">{status.data?.latestRun?.samplesStored ?? 0}</p>
        </Card>
        <Card className="p-4">
          <p className="text-xs uppercase text-slate-500">Open evidence gaps</p>
          <p className="mt-1 font-semibold">{status.data?.openGapCount ?? 0}</p>
        </Card>
      </div>

      <Card className="mb-4 p-5">
        <Label htmlFor="policy-subscription">Subscription</Label>
        {subscriptions.isPending ? (
          <Spinner />
        ) : subscriptions.error ? (
          <ErrorNote error={subscriptions.error} />
        ) : (
          <Select
            id="policy-subscription"
            value={selectedId}
            onChange={(event) => setSelectedId(event.target.value)}
          >
            <option value="">Choose a subscription…</option>
            {subscriptions.data.map((subscription) => (
              <option key={subscription.id} value={subscription.id}>
                {subscription.name} — {subscription.clientName}
              </option>
            ))}
          </Select>
        )}
      </Card>

      {!selectedId ? (
        <Card>
          <EmptyState
            icon={<DatabaseZap className="size-10" />}
            title="Choose a subscription"
            description="Inspect its current billing period, raw counter evidence, gaps, incidents and enforcement state."
          />
        </Card>
      ) : detail.isPending ? (
        <Spinner />
      ) : detail.error ? (
        <ErrorNote error={detail.error} />
      ) : detail.data ? (
        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-4">
            <Card className="p-4">
              <p className="text-xs uppercase text-slate-500">Transfer evidence</p>
              <Badge tone={detail.data.transfer.availability === 'available' ? 'green' : 'yellow'}>
                {detail.data.transfer.availability.replace('_', ' ')}
              </Badge>
            </Card>
            <Card className="p-4">
              <p className="text-xs uppercase text-slate-500">Ingress</p>
              <p className="mt-1 font-semibold">
                {transferValue(detail.data.transfer.ingressBytes)}
              </p>
            </Card>
            <Card className="p-4">
              <p className="text-xs uppercase text-slate-500">Egress</p>
              <p className="mt-1 font-semibold">
                {transferValue(detail.data.transfer.egressBytes)}
              </p>
            </Card>
            <Card className="p-4">
              <p className="text-xs uppercase text-slate-500">Allowance</p>
              <p className="mt-1 font-semibold">{bytes(detail.data.transfer.allowanceBytes)}</p>
            </Card>
          </div>

          {(detail.data.deploymentBlocked || detail.data.policySuspended) && (
            <Card className="border-amber-300 p-4">
              <div className="flex items-center gap-2 font-medium text-amber-800 dark:text-amber-200">
                <AlertTriangle className="size-4" />
                {detail.data.policySuspended
                  ? 'Suspended by service policy'
                  : 'New deployments blocked by service policy'}
              </div>
              <p className="mt-1 text-xs text-slate-500">
                {detail.data.policySuspensionReason ?? detail.data.deploymentBlockedReason}
              </p>
              {detail.data.policySuspended && (
                <Button
                  className="mt-3"
                  variant="secondary"
                  loading={reactivate.isPending}
                  onClick={() => reactivate.mutate()}
                >
                  Reactivate after issue is cleared
                </Button>
              )}
            </Card>
          )}

          <Card className="p-5">
            <h2 className="mb-3 font-semibold">Expiry extension &amp; reactivation</h2>
            <div className="grid gap-3 sm:grid-cols-[1fr_2fr_auto]">
              <div>
                <Label>New expiry date</Label>
                <Input type="date" value={expiresOn} onChange={(event) => setExpiresOn(event.target.value)} />
              </div>
              <div>
                <Label>Operator note</Label>
                <Input value={extensionNote} onChange={(event) => setExtensionNote(event.target.value)} />
              </div>
              <Button
                className="self-end"
                loading={extend.isPending}
                disabled={!expiresOn || !extensionNote}
                onClick={() => extend.mutate()}
              >
                Extend &amp; reactivate
              </Button>
            </div>
          </Card>

          <Card>
            <div className="border-b border-slate-200 px-5 py-4 dark:border-slate-800">
              <h2 className="font-semibold">Policy incidents</h2>
            </div>
            {detail.data.incidents.length === 0 ? (
              <p className="p-5 text-sm text-slate-500">No incidents.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead className="border-b text-xs uppercase text-slate-500">
                    <tr>
                      <th className="px-5 py-3">Type</th>
                      <th className="px-5 py-3">Status</th>
                      <th className="px-5 py-3">Observed / threshold</th>
                      <th className="px-5 py-3">Action</th>
                      <th className="px-5 py-3">Acknowledgement</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y dark:divide-slate-800">
                    {detail.data.incidents.map((incident) => (
                      <tr key={incident.id}>
                        <td className="px-5 py-3">{incident.kind.replaceAll('_', ' ')}</td>
                        <td className="px-5 py-3">
                          <Badge tone={statusTone(incident.status)}>{incident.status}</Badge>
                        </td>
                        <td className="px-5 py-3 tabular-nums">
                          {bytes(incident.observedValue)} / {bytes(incident.thresholdValue)}
                        </td>
                        <td className="px-5 py-3">{incident.enforcement.replace('_', ' ')}</td>
                        <td className="px-5 py-3">
                          {incident.acknowledgedAt ? (
                            <span className="text-slate-500">{incident.acknowledgementNote}</span>
                          ) : (
                            <Button
                              variant="ghost"
                              onClick={() => {
                                const note = window.prompt('Acknowledgement note');
                                if (note) acknowledge.mutate({ id: incident.id, note });
                              }}
                            >
                              Acknowledge
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          <Card>
            <div className="border-b border-slate-200 px-5 py-4 dark:border-slate-800">
              <h2 className="font-semibold">Transfer totals by resource</h2>
              <p className="mt-1 text-xs text-slate-500">
                Exact allocated bytes for{' '}
                {policyDateTime(detail.data.transfer.periodStart, transferTimeZone)}
                {' – '}
                {policyDateTime(detail.data.transfer.periodEnd, transferTimeZone)}.
              </p>
            </div>
            {detail.data.transfer.resourceTotals.length === 0 ? (
              <p className="p-5 text-sm text-slate-500">No attributable resource totals.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead className="border-b text-xs uppercase text-slate-500">
                    <tr>
                      <th className="px-5 py-3">Resource</th>
                      <th className="px-5 py-3">Ingress</th>
                      <th className="px-5 py-3">Egress</th>
                      <th className="px-5 py-3">Total</th>
                      <th className="px-5 py-3">Samples</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y dark:divide-slate-800">
                    {detail.data.transfer.resourceTotals.map((resource) => (
                      <tr key={resource.resourceId}>
                        <td className="px-5 py-3 font-medium">{resource.resourceName}</td>
                        <td className="px-5 py-3 tabular-nums">
                          {exactBytes(resource.ingressBytes)}
                        </td>
                        <td className="px-5 py-3 tabular-nums">
                          {exactBytes(resource.egressBytes)}
                        </td>
                        <td className="px-5 py-3 tabular-nums">
                          {exactBytes(resource.totalBytes)}
                        </td>
                        <td className="px-5 py-3 tabular-nums">{resource.evidenceSamples}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          <Card>
            <div className="border-b border-slate-200 px-5 py-4 dark:border-slate-800">
              <h2 className="font-semibold">Raw counter evidence</h2>
              <p className="mt-1 text-xs text-slate-500">
                Showing {detail.data.transfer.evidence.length} of{' '}
                {detail.data.transfer.evidenceSampleCount} retained samples for this billing period
                {detail.data.transfer.evidenceTruncated ? ', newest first' : ''}.
              </p>
            </div>
            {detail.data.transfer.evidence.length === 0 ? (
              <p className="p-5 text-sm text-slate-500">No counter samples in this period.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead className="border-b text-xs uppercase text-slate-500">
                    <tr>
                      <th className="px-5 py-3">Sample time</th>
                      <th className="px-5 py-3">Container</th>
                      <th className="px-5 py-3">Raw NET I/O</th>
                      <th className="px-5 py-3">Parsed ingress / egress</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y dark:divide-slate-800">
                    {detail.data.transfer.evidence.map((sample) => (
                      <tr key={sample.id}>
                        <td className="px-5 py-3 whitespace-nowrap">
                          {policyDateTime(sample.sampledAt, transferTimeZone)}
                        </td>
                        <td className="px-5 py-3">
                          <span className="font-medium">{sample.containerName}</span>
                          <span className="block font-mono text-xs text-slate-500">
                            {sample.source}
                          </span>
                        </td>
                        <td className="px-5 py-3 font-mono text-xs">
                          {sample.rawNetIo || 'Unavailable in retained sample'}
                        </td>
                        <td className="px-5 py-3 whitespace-nowrap tabular-nums">
                          {exactBytes(sample.ingressBytes)} / {exactBytes(sample.egressBytes)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          <Card className="p-5">
            <h2 className="mb-2 font-semibold">Collection gaps</h2>
            {detail.data.transfer.gaps.length === 0 ? (
              <p className="text-sm text-slate-500">No gaps overlap this billing period.</p>
            ) : (
              <ul className="space-y-2 text-sm">
                {detail.data.transfer.gaps.map((gap) => (
                  <li key={gap.id} className="rounded-md bg-amber-50 p-2 dark:bg-amber-950/30">
                    <span className="font-medium">{gap.kind.replaceAll('_', ' ')}</span> —{' '}
                    {gap.detail}
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>
      ) : null}
    </div>
  );
}
